import React, { Component } from 'react';
import PropTypes from 'prop-types';
import FieldsContainer from '../fields/FieldsContainer';
import DropdownField from '../fields/DropdownField';
import ClientField from '../fields/ClientField';
import TextField from '../fields/TextField';
import DateTimeField from '../fields/DateTimeField';
import TextArrayField from '../fields/TextArrayField';
import CheckBox from '../fields/CheckBox';
import { buySellOptions } from '../../utility/commonFieldOptions';
import { toUI } from '../../utility/mapStaticData';
import { extractNumber, addCommasToNum } from '../../utility/fieldFormatters';
import { validateAllFields, clearErrorsAllFields, FIELD_TYPE } from '../../utility/validators';

import './RFQDetail.css';

class RFQDetailComponent extends Component {
  constructor(props) {
    super(props);
    this.fields = {};
  }

  static get propTypes() {
    return {
      record: PropTypes.object,
      editing: PropTypes.bool,
      clipboardDate: PropTypes.any,
      recentlyUsed: PropTypes.object,
      staticData: PropTypes.object,
      onUpdateProperty: PropTypes.func.isRequired,
      onSetCurrentTime: PropTypes.func,
      onFetchClients: PropTypes.func,
      onCopyDate: PropTypes.func,
      onSetAcceptedTime: PropTypes.func,
      onFetchNotional: PropTypes.func,
    };
  }

  validate() {
    return validateAllFields(this.fields);
  }

  clearErrors() {
    clearErrorsAllFields(this.fields);
  }

  render() {
    const { record, editing, staticData, onUpdateProperty,
      onFetchClients, onSetCurrentTime, recentlyUsed,
      clipboardDate, onCopyDate, onSetAcceptedTime, onFetchNotional } = this.props;
    return (
      <div className="RFQDetail">
        <FieldsContainer>
          <div className="FieldsContainer-group">
            <div className="RecordDetail-id">
              {record.RFQNumber ? `RFQ ${record.RFQNumber}` : 'New RFQ'}
            </div>

            <DropdownField
              name="business-group"
              label="Business Group"
              editing={editing}
              required
              ref={(instance) => { this.fields.businessGroup = instance; }}
              value={record.BusinessGroup}
              possibleValues={staticData.businessGroups}
              mapFn={toUI.businessGroup}
              onChange={updatedValue => onUpdateProperty('BusinessGroup', updatedValue)}
              toolTip="Sales desk where Order/RFQ is registered"
            />
          </div>

          <DateTimeField
            name="receivedat"
            label="Quote requested"
            editing={editing}
            required
            ref={(instance) => { this.fields.receivedat = instance; }}
            onChange={updatedValue => onUpdateProperty('ReceivedAt', updatedValue)}
            timeZones={staticData.timeZones}
            value={record.ReceivedAt}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('ReceivedAt', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
            toolTip="Date and timestamp of request for quote"
          />
          {record.ReceivedAt && <DateTimeField
            name="providedat"
            label="Quote provided"
            editing={editing}
            required={!!record.AcceptedAt}
            ref={(instance) => { this.fields.providedat = instance; }}
            onChange={updatedValue => onUpdateProperty('ProvidedAt', updatedValue)}
            timeZones={staticData.timeZones}
            value={record.ProvidedAt}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('ProvidedAt', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
          />}
          {record.ProvidedAt && <DateTimeField
            name="modifiedat"
            label="Quote modified"
            editing={editing}
            required={false}
            ref={(instance) => { this.fields.modifiedat = instance; }}
            onChange={updatedValue => onUpdateProperty('ModifiedAt', updatedValue)}
            timeZones={staticData.timeZones}
            value={record.ModifiedAt}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('ModifiedAt', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
          />}
          {record.ProvidedAt && <DateTimeField
            name="acceptedat"
            label="Client accepted"
            editing={editing}
            required={!!record.ExecutedAt}
            ref={(instance) => { this.fields.acceptedat = instance; }}
            onChange={updatedValue => onUpdateProperty('AcceptedAt', updatedValue)}
            timeZones={staticData.timeZones}
            value={record.AcceptedAt}
            onSetCurrentTime={timeZoneName => onSetAcceptedTime(timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
          />}
          {record.AcceptedAt && <DateTimeField
            name="executedat"
            label="Quote executed"
            editing={editing}
            required={false}
            ref={(instance) => { this.fields.executedat = instance; }}
            onChange={updatedValue => onUpdateProperty('ExecutedAt', updatedValue)}
            timeZones={staticData.timeZones}
            value={record.ExecutedAt}
            onSetCurrentTime={timeZoneName => onSetCurrentTime('ExecutedAt', timeZoneName)}
            clipboardDate={clipboardDate}
            onCopy={onCopyDate}
          />}

          <TextField
            name="instrument-description"
            label="Instrument Description"
            ref={(instance) => { this.fields.instrumentDescription = instance; }}
            editing={editing}
            value={record.InstrumentDescription}
            multiLine
            onChange={updatedValue => onUpdateProperty('InstrumentDescription', updatedValue)}
          />

          <ClientField
            name="client"
            editing={editing}
            recentlyUsed={recentlyUsed.Client}
            required
            ref={(instance) => { this.fields.client = instance; }}
            onFetchClients={(input, type) => onFetchClients(input, type)}
            value={record.Client}
            onChange={updatedValue => onUpdateProperty('Client', updatedValue)}
            toolTip="Name of client (Start search with ^ to match from the start of Client names)"
          />

          <div className="FieldsContainer-group">
            <DropdownField
              name="buy-sell"
              label="Client Buy/Sell"
              required
              ref={(instance) => { this.fields.buysell = instance; }}
              editing={editing}
              value={record.Verb}
              possibleValues={buySellOptions}
              mapFn={toUI.buySell}
              onChange={updatedValue => onUpdateProperty('Verb', updatedValue)}
              toolTip="Buy/sell indicator from client perspective. Refer to user guide for more details"
            />
            <div className="QtyUnits">
              <TextField
                name="quantity"
                label="Quantity"
                required
                ref={(instance) => { this.fields.quantity = instance; }}
                editing={editing}
                value={addCommasToNum(record.Size)}
                validationMessage={'Please enter a valid number'}
                inputType={FIELD_TYPE.COMMA_NUMERIC}
                onChange={updatedValue => onUpdateProperty('Size', extractNumber(updatedValue))}
                toolTip="Size per unit bought/sold"
                onLoseFocus={onFetchNotional}
              />
              <DropdownField
                name="unit"
                label="Unit"
                editing={editing}
                value={record.UnitType}
                possibleValues={staticData.unitTypes}
                mapFn={toUI.unitType}
                onChange={(updatedValue) => { onUpdateProperty('UnitType', updatedValue); onFetchNotional(null, updatedValue); }}
                toolTip="Unit description"
              />
            </div>
          </div>

          <div className="FieldsContainer-group">
            <TextField
              name="notional"
              label="Notional"
              required
              ref={(instance) => { this.fields.notional = instance; }}
              editing={false}
              value={addCommasToNum(record.Notional, 2)}
              inputType={FIELD_TYPE.COMMA_NUMERIC}
              toolTip="Notional"
            />
            <div className="QtyUnits">
              <DropdownField
                name="currency"
                label="Currency"
                required
                editing={editing}
                ref={(instance) => { this.fields.currency = instance; }}
                value={record.Currency}
                possibleValues={staticData.currencies}
                mapFn={toUI.currency}
                onChange={updatedValue => onUpdateProperty('Currency', updatedValue)}
                toolTip="Main currency"
              />
              <CheckBox
                name="ecbExclusion"
                label="ECB Exclusion"
                ref={(instance) => { this.fields.ecbExclusion = instance; }}
                editing={editing}
                value={record.ECBExclusion}
                onChange={updatedValue => onUpdateProperty('ECBExclusion', updatedValue)}
                toolTip="ECB Exclusion from Pre Trade Transparency"
              />
            </div>
          </div>
          <div className="FieldsContainer-group">
            <TextField
              name="price"
              label="Price"
              required
              ref={(instance) => { this.fields.price = instance; }}
              editing={editing}
              value={addCommasToNum(record.Price, 6)}
              validationMessage={'Please enter a valid price'}
              inputType={FIELD_TYPE.COMMA_NUMERIC}
              onChange={updatedValue => onUpdateProperty('Price', extractNumber(updatedValue))}
              onLoseFocus={onFetchNotional}
            />

            <DropdownField
              name="priceType"
              label="Price Type"
              editing={editing}
              ref={(instance) => { this.fields.priceType = instance; }}
              value={record.PriceType}
              possibleValues={staticData.priceTypes}
              mapFn={toUI.priceType}
              onChange={updatedValue => onUpdateProperty('PriceType', updatedValue)}
              toolTip="Variable on price (Fixed, percentage or based on interest rate)"
            />
          </div>
          <div className="FieldsContainer-group">
            <DropdownField
              name="sales-person"
              label="Sales Person"
              editing={editing}
              required
              ref={(instance) => { this.fields.salesPerson = instance; }}
              value={record.SalesPerson}
              recentlyUsed={recentlyUsed.SalesPerson}
              possibleValues={staticData.salesPeople}
              mapFn={toUI.salesPerson}
              onChange={updatedValue => onUpdateProperty('SalesPerson', updatedValue)}
              toolTip="Sales person entering trade"
            />
            <DropdownField
              name="trader"
              label="Trader"
              editing={editing}
              ref={(instance) => { this.fields.trader = instance; }}
              value={record.Trader}
              recentlyUsed={recentlyUsed.Trader}
              possibleValues={staticData.traders}
              mapFn={toUI.trader}
              onChange={updatedValue => onUpdateProperty('Trader', updatedValue)}
              toolTip="Trader executing RFQ"
            />
          </div>
          <div className="FieldsContainer-group">
            <DropdownField
              name="front-office-system"
              label="Front Office System"
              editing={editing}
              value={record.FrontOfficeSystem}
              possibleValues={staticData.tradingSystems}
              mapFn={toUI.tradingSystem}
              onChange={updatedValue => onUpdateProperty('FrontOfficeSystem', updatedValue)}
              toolTip="Trading system"
            />

            <TextArrayField
              name="ticket-references"
              label="Ticket References"
              ref={(instance) => { this.fields.ticketReferences = instance; }}
              editing={editing}
              value={record.TicketReferences}
              onChange={updatedValue => onUpdateProperty('TicketReferences', updatedValue)}
              toolTip="Trade ID of FO System"
            />
          </div>
        </FieldsContainer>
      </div>
    );
  }
}

export default RFQDetailComponent;



// WEBPACK FOOTER //
// ./src/recordDetail/rfqDetail/RFQDetail.js